import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import * as echarts from 'echarts/core';
import { useLatest, useMount } from 'ahooks';
import { Icon } from 'tea-component';
import useChart from '@intelligence/v3/_hooks/chart/useChart';
import { fetchGeoJSON } from '@intelligence/services/common';
import { mapZoom, getPiecesDefault, getWorldMapDynamicOption } from './utils';
import { Zoom } from '@intelligence/enums/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { MAP_CHART_STATIC_OPTION } from './constants';

import type { IBaseProps } from '@home/types/common';
import type { GeoJSON } from '@intelligence/types/common';

/**
 * echarts世界地图组件props类型
 */
interface IMapChart extends IBaseProps {
  /**
   * 当前语言环境，默认英文
   */
  locale?: string;
  /**
   * 是否加载中
   */
  loading?: boolean;
  /**
   * 地区国家列表数据
   */
  regions?: IRegion[] | null;
  /**
   * echarts世界地区series的data数据
   */
  data?: IMapChartData[] | null;
  /**
   * 获取echarts中VisualMapPiecewise的Pieces配置，具体定义参见echarts文档
   */
  getPieces?: (list?: number[]) => any[];
  /**
   * 世界地图气泡浮窗formatter
   */
  tooltipFormatter?: Function;
  /**
   * 刷新依赖项
   */
  updateDeps?: React.DependencyList;
}

const MapChart: React.FC<IMapChart> = (props) => {
  const {
    style,
    className,
    locale = LANGUAGE_EN,
    loading = true,
    regions,
    data,
    getPieces = getPiecesDefault,
    tooltipFormatter,
    updateDeps,
  } = props;

  const tooltipFormatterLatest = useLatest(tooltipFormatter);
  const getPiecesLatest = useLatest(getPieces);

  const { chartNode, chartInstance, option, setOption } = useChart({ locale, initialOption: MAP_CHART_STATIC_OPTION });

  // 是否正在加载地图数据
  const [geoJSONLoading, setGeoJSONLoading] = useState(true);

  const updateMapChartOption = (list?: IMapChartData[] | null) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) return;

    const dynamicOption = getWorldMapDynamicOption({
      tooltipFormatter: tooltipFormatterLatest.current,
      pieces: getPiecesLatest.current?.(list?.map((item) => item?.value ?? Number.NaN)),
      data: list || [],
    });

    const newOption: any = {
      ...option,
      ...dynamicOption,
      tooltip: {
        ...option?.tooltip,
        ...dynamicOption?.tooltip,
      },
      visualMap: [
        {
          // @ts-ignore
          ...option?.visualMap?.[0],
          ...dynamicOption?.visualMap?.[0],
        },
      ],
      series: [
        {
          // @ts-ignore
          ...option?.series?.[0],
          ...dynamicOption?.series?.[0],
        },
      ],
    };

    setOption(newOption);
  };

  /**
   * 注册echarts世界地图数据
   */
  const registerEChartsMapGeoJSON = async () => {
    // echarts已注册世界地图数据，则不做任何处理
    const echartsWorldMap = echarts.getMap('world');
    if (!!echartsWorldMap) {
      setGeoJSONLoading(false);
      return;
    }

    // 从服务端拉取GeoJSON数据并注册echarts地图数据
    setGeoJSONLoading(true);
    try {
      const geoJSON = (await fetchGeoJSON()) as GeoJSON;
      echarts.registerMap('world', geoJSON);
    } finally {
      setGeoJSONLoading(false);
    }
  };

  useEffect(() => {
    if (loading || geoJSONLoading) return;

    updateMapChartOption(data);
  }, [locale, loading, geoJSONLoading, regions, data, updateDeps]);

  useMount(() => {
    registerEChartsMapGeoJSON();
  });

  return (
    <div style={style} className={classnames('chart-con', className)}>
      <div className="echarts-line worldMap">
        {/* 地图容器dom */}
        <div className="mpGameMapMain" ref={chartNode}></div>
        {/* 地图缩放控件 */}
        <div className="control">
          <span
            className="control-icon"
            onClick={() => {
              mapZoom(chartInstance.current, Zoom.ZoomIn);
            }}
          >
            <Icon type="plus" />
          </span>
          <span
            className="control-icon"
            onClick={() => {
              mapZoom(chartInstance.current, Zoom.ZoomOut);
            }}
          >
            <Icon type="minus" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default MapChart;
